import {
  addDays,
  format,
  isBefore,
  isSameDay,
  parseISO,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import useSWR from "swr";

import { BookingType } from "../../data";
import { DayPassDateRange } from "../../offering/order_params";
import { hasSmartPricingSearchParam } from "../utils/has_smart_pricing_search_param";

export function useBestDayRatesQuery(
  range: { startDate?: Date; endDate?: Date },
  bookingType: BookingType,
  currency?: string | null,
  spaceId?: string,
  spaceLayoutId?: string
) {
  const params = new URLSearchParams();
  params.set("booking_type", bookingType);
  if (spaceLayoutId) {
    params.set("space_layout_id", spaceLayoutId);
  }
  if (hasSmartPricingSearchParam()) {
    params.set("smart_pricing", "true");
  }
  const datesParam = getDatesInBetween(range.startDate, range.endDate);
  const queryString = params.toString().concat(`&${datesParam.join("&")}`);

  const response = useSWR(
    datesParam && spaceId
      ? `/api/v2/spaces/${spaceId}/dynamic_rates/best_day_rates?${queryString}`
      : null
  );

  return {
    error: response.error,
    data: toBestDayRates(response.data, currency),
  };
}

export function getViewRange(
  viewingRange?: { startDate: Date; endDate: Date },
  internalValue?: DayPassDateRange
): {
  startDate: Date;
  endDate: Date;
} {
  if (viewingRange) {
    return viewingRange;
  }
  if (internalValue?.startDate && internalValue.endDate) {
    return {
      startDate: startOfMonth(parseISO(internalValue.startDate)),
      endDate: endOfMonth(parseISO(internalValue.endDate)),
    };
  }
  return {
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
  };
}

interface BestDayDates {
  bookingType: "daily" | "hourly";
  originalUnitPrice: string;
  options: {
    [date: string]: number;
  };
}

function toBestDayRates(data: any, currency?: string | null): BestDayDates {
  const optionsWithCurrency = { ...data?.options };
  for (const date in optionsWithCurrency) {
    if (!currency) {
      break;
    }
    if (Object.prototype.hasOwnProperty.call(optionsWithCurrency, date)) {
      const price = optionsWithCurrency[date];
      optionsWithCurrency[date] = price;
    }
  }

  return {
    bookingType: data?.booking_type,
    originalUnitPrice: data?.original_unit_price,
    options: optionsWithCurrency,
  };
}

function getDatesInBetween(startDate?: Date, endDate?: Date) {
  if (!startDate || !endDate) {
    return [];
  }

  const datesParam = [];
  for (
    let i = 0;
    isBefore(addDays(startDate, i), endDate) ||
    isSameDay(addDays(startDate, i), endDate);
    i++
  ) {
    const checkDate = addDays(startDate, i);
    datesParam.push(`dates[]=${format(checkDate, "yyyy-MM-dd")}`);
  }

  return datesParam;
}
